import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Calendar, Users, DollarSign, Mail, UserX, RefreshCw,
  Loader2, Share2, ExternalLink, Send, MessageSquare, Pencil, Ban, Trash2, AlertTriangle,
  UserPlus, ChevronDown, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import { formatEventDate, eventCalendarDate } from "@/lib/dates";
import TopNav from "@/components/TopNav";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "attendees", label: "Attendees" },
];

const SERIES_TABS = [
  { id: "overview", label: "Overview" },
  { id: "sessions", label: "Sessions" },
  { id: "attendees", label: "Pass holders" },
];

const currencySymbol = (c) => ({ USD: "$", EUR: "€", GBP: "£" }[(c || "USD").toUpperCase()] || "$");

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const HostManageEventPage = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Action modals
  const [kickTarget, setKickTarget] = useState(null); // attendee row
  const [kickRefund, setKickRefund] = useState("");
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Comp attendee ("Add for free") state.
  // When ``compContext.session_game_id`` is set we're comping into a specific
  // series session; otherwise we comp against the top-level event.
  const [compContext, setCompContext] = useState(null); // { session_game_id?: string, session_title?: string } | null
  const [compName, setCompName] = useState("");
  const [compEmail, setCompEmail] = useState("");
  const [compNote, setCompNote] = useState("");
  const [compBusy, setCompBusy] = useState(false);
  // Existing-user picker
  const [compSearch, setCompSearch] = useState("");
  const [compSearchResults, setCompSearchResults] = useState([]);
  const [compSearchLoading, setCompSearchLoading] = useState(false);
  const [compSelectedUser, setCompSelectedUser] = useState(null); // { user_id, name, email, picture }

  // Debounced user search
  useEffect(() => {
    if (!compContext) return;
    const q = compSearch.trim();
    if (q.length < 2) {
      setCompSearchResults([]);
      return;
    }
    let cancelled = false;
    setCompSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/host/users/search?q=${encodeURIComponent(q)}`, { headers });
        const data = res.ok ? await res.json() : [];
        if (!cancelled) setCompSearchResults(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setCompSearchResults([]);
      } finally {
        if (!cancelled) setCompSearchLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [compSearch, compContext]);

  const pickCompUser = (u) => {
    setCompSelectedUser(u);
    setCompName(u.name || "");
    setCompEmail(u.email || "");
    setCompSearch("");
    setCompSearchResults([]);
  };

  const clearCompSelection = () => {
    setCompSelectedUser(null);
    setCompName("");
    setCompEmail("");
  };

  const resetCompForm = () => {
    setCompContext(null);
    setCompName("");
    setCompEmail("");
    setCompNote("");
    setCompSearch("");
    setCompSearchResults([]);
    setCompSelectedUser(null);
  };

  const handleComp = async () => {
    if (!compName.trim()) {
      toast.error("Attendee name is required");
      return;
    }
    setCompBusy(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      // Series session comp = comp into the underlying game
      const targetType = compContext?.session_game_id ? "game" : type;
      const targetId = compContext?.session_game_id || id;
      const res = await fetch(`${API}/host/events/${targetType}/${targetId}/comp`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: compName.trim(),
          email: compEmail.trim() || null,
          note: compNote.trim() || null,
          user_id: compSelectedUser?.user_id || null,
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(j.detail || "Failed to add attendee");
        return;
      }
      toast.success(`Added ${j.name} for free`);
      resetCompForm();
      // Refresh the whole panel so the new record shows up in attendees + sessions
      fetchData();
    } catch (e) {
      toast.error("Network error");
    } finally {
      setCompBusy(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/events/${type}/${id}/manage`, { headers });
      if (res.status === 403) {
        toast.error("You don't host this event");
        navigate("/host");
        return;
      }
      if (res.status === 404) {
        toast.error("Event not found");
        navigate("/host");
        return;
      }
      if (!res.ok) {
        toast.error("Couldn't load event");
        return;
      }
      setData(await res.json());
    } catch (e) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }, [type, id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleKick = async () => {
    if (!kickTarget) return;
    setBusy(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const res = await fetch(`${API}/host/events/${type}/${id}/kick`, {
        method: "POST", headers,
        body: JSON.stringify({
          user_id: kickTarget.user_id,
          refund_amount: parseFloat(kickRefund) || 0,
          reason: null,
        }),
      });
      if (res.ok) {
        const j = await res.json();
        toast.success(j.refunded > 0
          ? `Removed · refunded ${currencySymbol(j.currency)}${j.refunded.toFixed(2)}`
          : "Attendee removed");
        setKickTarget(null);
        setKickRefund("");
        await fetchData();
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to remove attendee");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRefund = async () => {
    if (!refundTarget || !refundAmount) return;
    setBusy(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const res = await fetch(`${API}/host/events/${type}/${id}/refund`, {
        method: "POST", headers,
        body: JSON.stringify({
          user_id: refundTarget.user_id,
          amount: parseFloat(refundAmount),
          reason: null,
        }),
      });
      if (res.ok) {
        const j = await res.json();
        toast.success(`Credited ${currencySymbol(j.currency)}${j.amount.toFixed(2)} to ${refundTarget.user_name}`);
        setRefundTarget(null);
        setRefundAmount("");
        await fetchData();
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to issue refund");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleBroadcast = async () => {
    if (!broadcastSubject.trim() || !broadcastBody.trim()) {
      toast.error("Subject and message required");
      return;
    }
    setBusy(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const res = await fetch(`${API}/host/events/${type}/${id}/message-attendees`, {
        method: "POST", headers,
        body: JSON.stringify({ subject: broadcastSubject, message: broadcastBody }),
      });
      if (res.ok) {
        const j = await res.json();
        toast.success(`Email sent to ${j.sent} attendee${j.sent === 1 ? "" : "s"}`);
        setShowBroadcast(false);
        setBroadcastSubject("");
        setBroadcastBody("");
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to send");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCancelEvent = async () => {
    setBusy(true);
    try {
      const headers = await authHeaders();
      const path = type === "game" ? `/games/${id}/cancel`
        : type === "series" ? `/series/${id}/cancel`
        : `/trips/${id}/cancel`;
      const res = await fetch(`${API}${path}`, { method: "POST", headers });
      if (res.ok) {
        const j = await res.json().catch(() => ({}));
        const parts = [];
        if (j.notified) parts.push(`${j.notified} attendee${j.notified !== 1 ? "s" : ""} notified`);
        if (j.refunded) parts.push(`${j.refunded} refund${j.refunded !== 1 ? "s" : ""} issued`);
        if (j.future_sessions_cancelled) parts.push(`${j.future_sessions_cancelled} session${j.future_sessions_cancelled !== 1 ? "s" : ""} cancelled`);
        if (j.pass_holders_refunded) parts.push(`${j.pass_holders_refunded} pass holder${j.pass_holders_refunded !== 1 ? "s" : ""} credited`);
        if (j.bookings_refunded) parts.push(`${j.bookings_refunded} booking${j.bookings_refunded !== 1 ? "s" : ""} refunded`);
        toast.success(parts.length ? `Event cancelled — ${parts.join(", ")}` : "Event cancelled");
        setConfirmCancel(false);
        await fetchData();
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to cancel event");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (type !== "game") {
      toast.error("Only games can be deleted. Cancel the event instead.");
      return;
    }
    setBusy(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/games/${id}`, { method: "DELETE", headers });
      if (res.ok) {
        toast.success("Event deleted");
        navigate("/host");
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to delete event");
      }
    } finally {
      setBusy(false);
    }
  };

  const onShare = async () => {
    if (!data) return;
    const url = `${window.location.origin}${data.public_url}`;
    try {
      if (navigator.share) await navigator.share({ title: data.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Public link copied");
      }
    } catch (_) { /* user cancelled */ }
  };

  const typeLabel = type === "series" ? "Series" : type === "trip" ? "Trip" : "Game";

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopNav />
        <div className="max-w-5xl mx-auto px-4 py-12 flex items-center justify-center text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="host-manage-page">
        {/* Back + share */}
        <button
          onClick={() => navigate("/host")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
          data-testid="host-manage-back"
        >
          <ArrowLeft className="w-4 h-4" /> Host Hub
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {data.image_url && (
            <img
              src={data.image_url}
              alt=""
              className="w-full sm:w-40 h-32 sm:h-28 rounded-xl object-cover shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{typeLabel}</p>
            <h1 className="font-display text-2xl sm:text-3xl leading-tight truncate" data-testid="manage-title">
              {data.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(data.start_iso)}</span>
              {data.city && <span>· {data.city}</span>}
              {data.status === "cancelled" && (
                <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-700 text-[11px] font-medium">Cancelled</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:flex-col sm:items-end">
            <Button variant="outline" size="sm" onClick={onShare} className="gap-1.5" data-testid="manage-share-btn">
              <Share2 className="w-3.5 h-3.5" /> Share
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(data.public_url)} className="gap-1.5" data-testid="manage-view-public">
              <ExternalLink className="w-3.5 h-3.5" /> View public page
            </Button>
          </div>
        </div>

        {/* Tab strip */}
        <div className="border-b border-border mb-5" data-testid="manage-tabs">
          <div className="flex items-center gap-1">
            {(type === "series" ? SERIES_TABS : TABS).map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`manage-tab-${t.id}`}
                >
                  {t.label}
                  {t.id === "attendees" && (
                    <span className="ml-1.5 text-[11px] text-muted-foreground">({data.attendee_count})</span>
                  )}
                  {active && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />}
                </button>
              );
            })}
          </div>
        </div>

        {tab === "overview" && (
          <OverviewTab
            data={data}
            currency={data.currency}
            onMessageAll={() => setShowBroadcast(true)}
            onEdit={() => {
              // Route back to the full Create-flow page in edit mode
              const path = type === "game" ? "/create/game" : type === "series" ? "/create/series" : "/create/trip";
              navigate(`${path}?edit=${encodeURIComponent(id)}`);
            }}
            onCancel={() => setConfirmCancel(true)}
            onDelete={() => setConfirmDelete(true)}
            type={type}
          />
        )}
        {tab === "sessions" && type === "series" && (
          <SessionsTab
            sessions={data.sessions || []}
            navigate={navigate}
            onAddComp={(s) => setCompContext({ session_game_id: s.game_id, session_title: s.title })}
            onSessionsChanged={fetchData}
          />
        )}
        {tab === "attendees" && (
          <AttendeesTab
            data={data}
            onKick={(a) => { setKickTarget(a); setKickRefund(String(a.amount_paid || "")); }}
            onRefund={(a) => { setRefundTarget(a); setRefundAmount(""); }}
            onMessageAll={() => setShowBroadcast(true)}
            onAddComp={() => setCompContext({})}
          />
        )}
      </main>


      {/* COMP dialog — Host adds an attendee for free */}
      <Dialog open={!!compContext} onOpenChange={(o) => { if (!o) resetCompForm(); }}>
        <DialogContent data-testid="comp-dialog">
          <DialogHeader>
            <DialogTitle>Add attendee for free</DialogTitle>
            <DialogDescription>
              {compContext?.session_game_id
                ? <>Comping into session <span className="text-foreground font-medium">{compContext.session_title}</span>.</>
                : <>Give someone a free {type === "series" ? "Season Pass" : type === "trip" ? "trip booking" : "spot"} for {data?.title ? <span className="text-foreground font-medium">{data.title}</span> : "this event"}.</>}
              {" "}They won&apos;t be charged and it will not appear in revenue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Existing-user picker */}
            <div>
              <Label htmlFor="comp-search">Find an existing Pouls user</Label>
              {compSelectedUser ? (
                <div className="mt-1 flex items-center gap-3 p-2 pr-2.5 rounded-md border border-primary/40 bg-primary/5" data-testid="comp-selected-user">
                  <img
                    src={compSelectedUser.picture || `https://i.pravatar.cc/64?u=${encodeURIComponent(compSelectedUser.user_id)}`}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{compSelectedUser.name || "Unnamed"}</p>
                    {compSelectedUser.email && (
                      <p className="text-xs text-muted-foreground truncate">{compSelectedUser.email}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearCompSelection} data-testid="comp-clear-user">
                    Clear
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    id="comp-search"
                    value={compSearch}
                    onChange={(e) => setCompSearch(e.target.value)}
                    placeholder="Search by name or email…"
                    autoComplete="off"
                    data-testid="comp-search-input"
                  />
                  {compSearch.trim().length >= 2 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-md border border-border bg-background shadow-lg" data-testid="comp-search-results">
                      {compSearchLoading ? (
                        <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                        </div>
                      ) : compSearchResults.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-muted-foreground">
                          No users found. You can still fill in the name/email fields below to add them manually.
                        </div>
                      ) : (
                        compSearchResults.map((u) => (
                          <button
                            key={u.user_id}
                            type="button"
                            onClick={() => pickCompUser(u)}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted transition-colors text-left"
                            data-testid={`comp-search-result-${u.user_id}`}
                          >
                            <img
                              src={u.picture || `https://i.pravatar.cc/64?u=${encodeURIComponent(u.user_id)}`}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{u.name || "Unnamed"}</p>
                              {u.email && <p className="text-xs text-muted-foreground truncate">{u.email}</p>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Or fill in name / email below to add someone who isn&apos;t on Pouls yet.
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
                <span className="bg-background px-2 text-muted-foreground">Or add manually</span>
              </div>
            </div>

            <div>
              <Label htmlFor="comp-name">Name *</Label>
              <Input
                id="comp-name"
                value={compName}
                onChange={(e) => setCompName(e.target.value)}
                placeholder="e.g. Alex Morgan"
                disabled={!!compSelectedUser}
                data-testid="comp-name-input"
              />
            </div>
            <div>
              <Label htmlFor="comp-email">Email (optional)</Label>
              <Input
                id="comp-email"
                type="email"
                value={compEmail}
                onChange={(e) => setCompEmail(e.target.value)}
                placeholder="If provided, they can log in with this email"
                disabled={!!compSelectedUser}
                data-testid="comp-email-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                If the email matches an existing user, they&apos;ll see the booking in their profile.
              </p>
            </div>
            <div>
              <Label htmlFor="comp-note">Note (optional)</Label>
              <Input
                id="comp-note"
                value={compNote}
                onChange={(e) => setCompNote(e.target.value)}
                placeholder="e.g. VIP, referral, comp"
                data-testid="comp-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetCompForm} disabled={compBusy}>Cancel</Button>
            <Button onClick={handleComp} disabled={compBusy || !compName.trim()} data-testid="comp-confirm-btn">
              {compBusy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Adding…</> : <><UserPlus className="w-4 h-4 mr-1" /> Add for free</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KICK dialog */}
      <Dialog open={!!kickTarget} onOpenChange={(o) => !o && setKickTarget(null)}>
        <DialogContent data-testid="kick-dialog">
          <DialogHeader>
            <DialogTitle>Remove attendee</DialogTitle>
            <DialogDescription>
              {kickTarget?.user_name} will lose their spot. You can optionally credit their wallet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="kick-refund">Refund amount ({currencySymbol(kickTarget?.currency)})</Label>
              <Input
                id="kick-refund"
                type="number"
                min={0}
                value={kickRefund}
                onChange={(e) => setKickRefund(e.target.value)}
                placeholder="0"
                data-testid="kick-refund-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Original amount paid: {currencySymbol(kickTarget?.currency)}{Number(kickTarget?.amount_paid || 0).toFixed(2)}.
                Leave at 0 to remove without refund.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKickTarget(null)} disabled={busy}>Cancel</Button>
            <Button onClick={handleKick} disabled={busy} className="bg-red-500 text-white hover:bg-red-600" data-testid="kick-confirm-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Removing…</> : <><UserX className="w-4 h-4 mr-1" /> Remove attendee</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REFUND dialog */}
      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Issue wallet credit</DialogTitle>
            <DialogDescription>
              Credit {refundTarget?.user_name}&apos;s Pouls wallet. They keep their spot.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="refund-amount">Amount ({currencySymbol(data.currency)})</Label>
              <Input
                id="refund-amount"
                type="number"
                min={0}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="e.g. 10"
                data-testid="refund-amount-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)} disabled={busy}>Cancel</Button>
            <Button onClick={handleRefund} disabled={busy || !refundAmount} data-testid="refund-confirm-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Crediting…</> : <><RefreshCw className="w-4 h-4 mr-1" /> Credit wallet</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BROADCAST dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle>Message all attendees</DialogTitle>
            <DialogDescription>
              Sends an email to every confirmed attendee of &ldquo;{data.title}&rdquo;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="broadcast-subject">Subject</Label>
              <Input
                id="broadcast-subject"
                value={broadcastSubject}
                onChange={(e) => setBroadcastSubject(e.target.value)}
                placeholder="A quick update on tomorrow's game"
                data-testid="broadcast-subject-input"
              />
            </div>
            <div>
              <Label htmlFor="broadcast-body">Message</Label>
              <Textarea
                id="broadcast-body"
                rows={6}
                value={broadcastBody}
                onChange={(e) => setBroadcastBody(e.target.value)}
                placeholder="Hi everyone…"
                data-testid="broadcast-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcast(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleBroadcast} disabled={busy} className="gap-1.5" data-testid="broadcast-send-btn">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CANCEL confirm */}
      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent data-testid="cancel-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" /> Cancel this event?
            </DialogTitle>
            <DialogDescription>
              {type === "game" && "Attendees will be notified by email and any payments will be refunded to their Pouls wallets."}
              {type === "series" && "All future sessions will be cancelled and pass holders will be credited pro-rata."}
              {type === "trip" && "Every active booking will be fully refunded to the traveller's Pouls wallet."}
              {" "}This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(false)} disabled={busy}>Keep event</Button>
            <Button onClick={handleCancelEvent} disabled={busy} className="bg-red-500 text-white hover:bg-red-600" data-testid="cancel-event-confirm-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Cancelling…</> : <><Ban className="w-4 h-4 mr-1" /> Cancel event</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid="delete-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" /> Delete this event?
            </DialogTitle>
            <DialogDescription>
              Permanently removes the event and every reservation. Players who paid will lose their booking record. Consider cancelling instead if you want to keep the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={busy}>Keep event</Button>
            <Button onClick={handleDeleteEvent} disabled={busy} className="bg-red-500 text-white hover:bg-red-600" data-testid="delete-event-confirm-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Deleting…</> : <><Trash2 className="w-4 h-4 mr-1" /> Delete forever</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent = "bg-primary/15 text-primary", testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="flex items-start justify-between mb-2">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="font-display text-2xl">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </Card>
);

const OverviewTab = ({ data, currency, onMessageAll, onEdit, onCancel, onDelete, type }) => {
  const fillPct = data.max_count > 0 ? Math.min(100, Math.round((data.attendee_count / data.max_count) * 100)) : 0;
  const isCancelled = data.status === "cancelled";
  return (
    <div className="space-y-5" data-testid="overview-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          icon={Users}
          label="Attendees"
          value={`${data.attendee_count}${data.max_count ? ` / ${data.max_count}` : ""}`}
          sub={data.max_count ? `${fillPct}% full` : "uncapped"}
          accent="bg-blue-500/15 text-blue-700 dark:text-blue-300"
          testid="overview-attendees"
        />
        <StatCard
          icon={DollarSign}
          label="Revenue"
          value={`${currencySymbol(currency)}${data.revenue_total.toLocaleString()}`}
          sub="paid so far"
          accent="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
          testid="overview-revenue"
        />
        <StatCard
          icon={Calendar}
          label="Status"
          value={isCancelled ? "Cancelled" : "Active"}
          sub={data.start_iso ? formatDate(data.start_iso) : ""}
          accent="bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300"
          testid="overview-status"
        />
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display text-lg">Quick actions</h2>
            <p className="text-xs text-muted-foreground">Common host workflows for this event</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={onEdit}
            disabled={isCancelled}
            className="justify-start h-auto py-3 gap-3"
            data-testid="overview-edit-event"
          >
            <Pencil className="w-4 h-4 shrink-0" />
            <div className="text-left">
              <p className="text-sm font-medium">Edit event</p>
              <p className="text-xs text-muted-foreground">Change title, date, price and more</p>
            </div>
          </Button>
          <Button variant="outline" onClick={onMessageAll} className="justify-start h-auto py-3 gap-3" data-testid="overview-msg-all">
            <Mail className="w-4 h-4 shrink-0" />
            <div className="text-left">
              <p className="text-sm font-medium">Message all attendees</p>
              <p className="text-xs text-muted-foreground">Send a broadcast email via Pouls</p>
            </div>
          </Button>
        </div>
      </Card>

      {/* Danger zone */}
      <Card className="p-4 border-red-200 dark:border-red-900/40">
        <div className="mb-3">
          <h2 className="font-display text-lg flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertTriangle className="w-4 h-4" /> Danger zone
          </h2>
          <p className="text-xs text-muted-foreground">Cancelling refunds attendees automatically. Deletion is permanent.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isCancelled}
            className="justify-start h-auto py-3 gap-3 border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30"
            data-testid="overview-cancel-event"
          >
            <Ban className="w-4 h-4 shrink-0" />
            <div className="text-left">
              <p className="text-sm font-medium">{isCancelled ? "Already cancelled" : "Cancel event"}</p>
              <p className="text-xs text-muted-foreground">
                {type === "game" && "Notifies attendees and refunds payments to wallets."}
                {type === "series" && "Cancels all future sessions and credits pass holders pro-rata."}
                {type === "trip" && "Fully refunds every booking to the traveller's wallet."}
              </p>
            </div>
          </Button>
          {type === "game" && (
            <Button
              variant="outline"
              onClick={onDelete}
              className="justify-start h-auto py-3 gap-3 border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30"
              data-testid="overview-delete-event"
            >
              <Trash2 className="w-4 h-4 shrink-0" />
              <div className="text-left">
                <p className="text-sm font-medium">Delete event</p>
                <p className="text-xs text-muted-foreground">Permanently removes the event and all its reservations.</p>
              </div>
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

const SessionsTab = ({ sessions, navigate, onAddComp, onSessionsChanged }) => {
  const [query, setQuery] = useState("");
  const [showPast, setShowPast] = useState(false);

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isFuture = (iso) => {
    const d = eventCalendarDate(iso);
    if (!d) return true;
    return d >= todayStart;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (sessions || []).filter((s) => {
      if (!showPast && !isFuture(s.date)) return false;
      if (q && !`${s.title || ""} ${s.location_name || ""} ${s.city || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sessions, query, showPast]);

  if (!sessions || sessions.length === 0) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">No sessions yet for this series.</Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="host-sessions-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search sessions…"
          className="w-full sm:w-72 h-9"
          data-testid="sessions-search"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={showPast}
            onChange={(e) => setShowPast(e.target.checked)}
            data-testid="sessions-show-past"
          />
          Show past sessions
        </label>
        <span className="ml-auto text-xs text-muted-foreground">
          {filtered.length} of {sessions.length} session{sessions.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">No sessions match this filter.</Card>
        ) : filtered.map((s) => (
          <SessionRow
            key={s.game_id}
            session={s}
            isPast={!isFuture(s.date)}
            navigate={navigate}
            onAddComp={onAddComp}
            onSessionsChanged={onSessionsChanged}
          />
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        Editing a single session only changes that specific one — the rest of the series is untouched. To edit the series-level details (title, description, host, pass price), use <span className="text-foreground font-medium">Overview → Edit event</span>.
      </p>
    </div>
  );
};

/**
 * One row in the Sessions tab. Expand to see attendees inline with per-row
 * Remove buttons — no navigation, no URL hunting.
 */
const SessionRow = ({ session: s, isPast, navigate, onAddComp, onSessionsChanged }) => {
  const cancelled = s.status === "cancelled";
  const [expanded, setExpanded] = useState(false);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [attendees, setAttendees] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const fmtDate = (iso) => formatEventDate(iso, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  const symbol = (c) => ({ USD: "$", EUR: "€", GBP: "£" }[(c || "USD").toUpperCase()] || "$");

  const loadAttendees = async () => {
    setAttendeesLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(`${API}/host/events/game/${s.game_id}/manage`, { headers });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Failed to load attendees");
        setAttendees([]);
        return;
      }
      const data = await resp.json();
      setAttendees(data.attendees || []);
    } catch (e) {
      toast.error("Network error");
      setAttendees([]);
    } finally {
      setAttendeesLoading(false);
    }
  };

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && attendees === null) {
      loadAttendees();
    }
  };

  const handleRemove = async (a) => {
    const label = a.user_name || a.user_email || "this attendee";
    if (!window.confirm(`Remove ${label} from this session? This can't be undone.`)) return;
    setRemovingId(a.reservation_id || a.user_id);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const resp = await fetch(`${API}/host/events/game/${s.game_id}/kick`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: a.user_id,
          refund_amount: 0,
          reason: "Removed via Host Hub",
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(data.detail || "Failed to remove attendee");
        return;
      }
      toast.success(`Removed ${label}`);
      // Refresh both the local attendees list and the parent's session counters
      await loadAttendees();
      if (onSessionsChanged) onSessionsChanged();
    } catch (e) {
      toast.error("Network error");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card className={`overflow-hidden ${cancelled ? "opacity-60" : ""}`} data-testid={`session-row-${s.game_id}`}>
      {/* Header row (clickable to expand) */}
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          type="button"
          onClick={toggleExpand}
          className="flex-1 min-w-0 flex items-start gap-2 text-left"
          data-testid={`session-toggle-${s.game_id}`}
        >
          <span className="mt-0.5 shrink-0 text-muted-foreground">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold truncate">{s.title}</span>
              {isPast && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Past</span>}
              {cancelled && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Cancelled</span>}
            </span>
            <span className="block text-xs text-muted-foreground mt-0.5">
              {fmtDate(s.date)} · {s.time_start}–{s.time_end}
              {s.location_name ? ` · ${s.location_name}` : ""}
              {s.city ? `, ${s.city}` : ""}
            </span>
            <span className="block text-xs text-muted-foreground mt-0.5">
              {s.current_players}/{s.max_players} players · {s.price > 0 ? `${symbol(s.currency)}${s.price}` : "Free"}
            </span>
          </span>
        </button>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddComp && onAddComp(s)}
            className="gap-1.5"
            disabled={cancelled || (s.max_players && s.current_players >= s.max_players)}
            title={s.max_players && s.current_players >= s.max_players ? "Session is full" : "Add someone for free"}
            data-testid={`session-comp-${s.game_id}`}
          >
            <UserPlus className="w-3.5 h-3.5" />
            Add
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/game/${s.game_id}`)}
            className="gap-1.5"
            data-testid={`session-view-${s.game_id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View
          </Button>
          <Button
            size="sm"
            onClick={() => navigate(`/create/game?edit=${encodeURIComponent(s.game_id)}`)}
            className="gap-1.5"
            disabled={cancelled}
            data-testid={`session-edit-${s.game_id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </Button>
        </div>
      </div>

      {/* Expanded attendee list */}
      {expanded && (
        <div className="border-t border-border bg-muted/40 px-4 py-3" data-testid={`session-attendees-${s.game_id}`}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Attendees</p>
          {attendeesLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          ) : (attendees || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No attendees yet. Use the Add button above to comp someone in.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-background overflow-hidden">
              {(attendees || []).map((a) => {
                const isComp = a.payment_method === "comp" || a.amount_paid === 0;
                const rowId = a.reservation_id || a.user_id;
                return (
                  <li
                    key={rowId}
                    className="flex items-center gap-3 px-3 py-2.5"
                    data-testid={`session-attendee-${s.game_id}-${a.user_id}`}
                  >
                    <img
                      src={a.user_picture || `https://i.pravatar.cc/64?u=${encodeURIComponent(a.user_id || rowId)}`}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {a.user_name || "Guest"}
                        {a.is_fake && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">Fake</span>}
                        {isComp && !a.is_fake && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-500/10 px-1.5 py-0.5 rounded">Comp</span>}
                      </p>
                      {a.user_email && (
                        <p className="text-xs text-muted-foreground truncate">{a.user_email}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemove(a)}
                      disabled={removingId === rowId || cancelled}
                      className="gap-1.5"
                      data-testid={`session-remove-${s.game_id}-${a.user_id}`}
                    >
                      {removingId === rowId ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Removing…</>
                      ) : (
                        <><UserX className="w-3.5 h-3.5" /> Remove</>
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
};


const AttendeesTab = ({ data, onKick, onRefund, onMessageAll, onAddComp }) => {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!query) return data.attendees;
    const q = query.toLowerCase();
    return data.attendees.filter((a) =>
      (a.user_name || "").toLowerCase().includes(q) ||
      (a.user_email || "").toLowerCase().includes(q)
    );
  }, [data.attendees, query]);

  if (!data.attendees.length) {
    return (
      <Card className="p-8 text-center" data-testid="attendees-empty">
        <Users className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
        <p className="font-medium">No attendees yet</p>
        <p className="text-xs text-muted-foreground mt-1">Share your event link to start filling spots — or comp someone in below.</p>
        {onAddComp && (
          <Button size="sm" onClick={onAddComp} className="gap-1.5 mt-4" data-testid="attendees-add-comp-empty">
            <UserPlus className="w-3.5 h-3.5" /> Add attendee for free
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="attendees-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          placeholder="Search by name or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-sm"
          data-testid="attendees-search"
        />
        <div className="flex-1" />
        {onAddComp && (
          <Button variant="outline" size="sm" onClick={onAddComp} className="gap-1.5" data-testid="attendees-add-comp-btn">
            <UserPlus className="w-3.5 h-3.5" /> Add for free
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onMessageAll} className="gap-1.5" data-testid="attendees-msg-all-btn">
          <MessageSquare className="w-3.5 h-3.5" /> Message all
        </Button>
      </div>
      <Card className="divide-y divide-border">
        {filtered.map((a) => (
          <div key={a.ref_id} className="p-4 flex items-start gap-3" data-testid={`attendee-row-${a.user_id}`}>
            <img
              src={a.user_picture || `https://i.pravatar.cc/80?u=${encodeURIComponent(a.user_id)}`}
              alt=""
              className="w-10 h-10 rounded-full object-cover shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{a.user_name}</p>
              {a.user_email && (
                <p className="text-xs text-muted-foreground truncate">{a.user_email}</p>
              )}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-1">
                <span>Joined {a.joined_at ? new Date(a.joined_at).toLocaleDateString() : "—"}</span>
                <span>· Paid {currencySymbol(a.currency)}{Number(a.amount_paid || 0).toFixed(2)}</span>
                {a.extra?.payment_method === "season_pass" && <span>· Season pass</span>}
                {a.extra?.guest_count > 1 && <span>· {a.extra.guest_count} guests</span>}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost" size="sm"
                onClick={() => onRefund(a)}
                className="h-8 gap-1 text-emerald-600 hover:text-emerald-700"
                data-testid={`attendee-refund-${a.user_id}`}
              >
                <RefreshCw className="w-3.5 h-3.5" /> Credit
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => onKick(a)}
                className="h-8 gap-1 text-red-600 hover:text-red-700"
                data-testid={`attendee-kick-${a.user_id}`}
              >
                <UserX className="w-3.5 h-3.5" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
};

export default HostManageEventPage;
